import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import { controlDeferred } from '@eureka/utils/control';
import { accountDeferred, isUserLogin } from '@eureka/utils/account';
import { clientDeferred } from '@eureka/utils/client';
import { packageInfoDeferred } from '@eureka/utils/package-info';
import { rechargeInfoDeferred } from '@eureka/utils/recharge-info';
import type { UserInfoService } from '../user-info-service';
import type { IPackageType, RechargeEligibilityInfo } from '../../../../../services/src/apis/common.api.types';
import _ from 'lodash';

@injectable()
export class UserInfoServiceImpl implements UserInfoService {
    private _packageList: IPackageType[] = [];
    private _recharge: RechargeEligibilityInfo | null = null

    private async _ensureLoaded() {
        const loggedIn = await isUserLogin();
        if (loggedIn) {
            const [packageInfo, recharge] = await Promise.all([
                packageInfoDeferred.promise,
                rechargeInfoDeferred.promise
            ]);
            if (this._packageList.length === 0) {
                this._packageList = packageInfo
            }
            if (!this._recharge) {
                this._recharge = recharge
            }
        }
        const [control, account, client] = await Promise.all([
            controlDeferred.promise,
            accountDeferred.promise,
            clientDeferred.promise
        ]);
        return { control, account, client };
    }

    async getIsEnterpriseTenant() {
        const { account } = await this._ensureLoaded();
        return !!account._info.company_id;
    }

    async getIsPersonalTenant() {
        return !(await this.getIsEnterpriseTenant());
    }

    async getIsFreeUser() {
        const { control } = await this._ensureLoaded();
        const isPersonalTenant = await this.getIsPersonalTenant();
        return isPersonalTenant && control.freemium;
    }

    async getIsPro() {
        const isPersonalTenant = await this.getIsPersonalTenant();
        const isFreeUser = await this.getIsFreeUser();
        return isPersonalTenant && !isFreeUser;
    }

    async getIsFeiShu() {
        const { client } = await this._ensureLoaded();
        return client.isFeiShu;
    }

    async getIsCreditsAvailable() {
        const { control } = await this._ensureLoaded();
        return control.credit_package;
    }

    async getIsCountPackageAvailable() {
        const { control } = await this._ensureLoaded();
        return control.count_package;
    }

    async getIsEnterpriseCommercialUser() {
        const isEnterpriseTenant = await this.getIsEnterpriseTenant()
        return isEnterpriseTenant && this._packageList.some(item => item.package_type === 'COMMERCIAL');
    }

    async getIsEnterpriseFreeUser() {
        const isEnterpriseTenant = await this.getIsEnterpriseTenant()
        return isEnterpriseTenant && this._packageList.some(item => item.package_type === 'FREE');
    }

    async getIsTrialUser() {
        await this._ensureLoaded();
        return _.some(this._packageList, item => _.has(item, 'trial_package_left_days'))
    }
}
